// Custom Dropdown Functionality
class CustomDropdown {
    constructor(element) {
      this.dropdown = element;
      this.trigger = element.querySelector('.custom-dropdown__trigger');
      this.menu = element.querySelector('.custom-dropdown__menu');
      this.options = element.querySelectorAll('.custom-dropdown__option');
      this.hiddenInput = element.querySelector('input[type="hidden"]');
      this.selectedText = element.querySelector('.custom-dropdown__selected-text');
      
      this.isOpen = false;
      this.selectedValue = this.hiddenInput.value;
      
      this.init();
    }
    
    init() {
      // Set initial selected text and value
      this.updateSelectedText();
      
      // If there's already a selected option, use its value
      const selectedOption = this.dropdown.querySelector('.custom-dropdown__option--selected');
      if (selectedOption && !selectedOption.classList.contains('custom-dropdown__option--disabled')) {
        this.selectedValue = selectedOption.dataset.value;
        this.hiddenInput.value = this.selectedValue;
      }
      
      // Add event listeners
      this.trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle();
      });
      
      this.trigger.addEventListener('keydown', (e) => {
        this.handleKeydown(e);
      });
      
      // Add click listeners to options
      this.options.forEach(option => {
        option.addEventListener('click', (e) => {
          if (!option.classList.contains('custom-dropdown__option--disabled')) {
            this.selectOption(option);
          }
        });
      });
      
      // Close dropdown when clicking outside
      document.addEventListener('click', (e) => {
        if (!this.dropdown.contains(e.target)) {
          this.close();
        }
      });
      
      // Close dropdown on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });
    }
    
    toggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }
    
    open() {
      this.isOpen = true;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.menu.classList.add('custom-dropdown__menu--open');
      this.trigger.focus();
    }
    
    close() {
      this.isOpen = false;
      this.trigger.setAttribute('aria-expanded', 'false');
      this.menu.classList.remove('custom-dropdown__menu--open');
    }
    
    selectOption(option) {
      const value = option.dataset.value;
      console.log('selectOption called with value:', value);
      
      // Update selected value
      this.selectedValue = value;
      this.hiddenInput.value = value;
      
      // Update visual selection
      this.options.forEach(opt => opt.classList.remove('custom-dropdown__option--selected'));
      option.classList.add('custom-dropdown__option--selected');
      
      // Update selected text
      this.updateSelectedText();
      
      // Close dropdown
      this.close();
      
      // Trigger change event for form handling
      this.hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
      
      // Trigger Shopify variant change events
      this.triggerShopifyVariantChange(value);
    }
    
    triggerShopifyVariantChange(selectedValue) {
      console.log('triggerShopifyVariantChange called with:', selectedValue);
      
      // Find the variant radios/selects container
      const variantContainer = this.dropdown.closest('variant-radios, variant-selects');
      if (!variantContainer) {
        console.log('No variant container found');
        return;
      }
      console.log('Found variant container:', variantContainer);
      
      // Find the hidden input in our custom dropdown
      const hiddenInput = this.dropdown.querySelector('input[type="hidden"]');
      if (!hiddenInput) {
        console.log('No hidden input found');
        return;
      }
      console.log('Found hidden input:', hiddenInput);
      
      // Update the hidden input value
      hiddenInput.value = selectedValue;
      
      // Create a proper fieldset with radio button that VariantRadios expects
      const optionName = this.dropdown.dataset.optionName || 'Size';
      
      // Find or create the fieldset for this option
      let fieldset = variantContainer.querySelector(`fieldset[data-option-name="${optionName}"]`);
      if (!fieldset) {
        fieldset = document.createElement('fieldset');
        fieldset.setAttribute('data-option-name', optionName);
        fieldset.style.display = 'none'; // Hide the fieldset
        variantContainer.appendChild(fieldset);
      }
      
      // Create or find the radio button for this value
      let radioInput = fieldset.querySelector(`input[value="${selectedValue}"]`);
      if (!radioInput) {
        radioInput = document.createElement('input');
        radioInput.type = 'radio';
        radioInput.name = `options[${optionName}]`;
        radioInput.value = selectedValue;
        radioInput.id = `${optionName}-${selectedValue}`;
        fieldset.appendChild(radioInput);
      }
      
      // Uncheck all other radio buttons for this option
      const allRadiosForOption = variantContainer.querySelectorAll(`input[name="options[${optionName}]"]`);
      allRadiosForOption.forEach(radio => {
        radio.checked = false;
      });
      
      // Check the selected radio button
      radioInput.checked = true;
      console.log('Set radio button checked:', radioInput);
      
      // Trigger change events on the radio button
      radioInput.dispatchEvent(new Event('change', { bubbles: true }));
      
      // Also trigger change event on the hidden input
      hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
      
      // Trigger the variant container's change event to activate onVariantChange
      console.log('Triggering variant container change event');
      variantContainer.dispatchEvent(new Event('change', { bubbles: true }));
      
      // Also try to manually call onVariantChange if it exists
      if (variantContainer.onVariantChange) {
        console.log('Manually calling onVariantChange');
        variantContainer.onVariantChange();
      }
    }
    
    updateSelectedText() {
      const selectedOption = this.dropdown.querySelector('.custom-dropdown__option--selected');
      if (selectedOption && !selectedOption.classList.contains('custom-dropdown__option--disabled')) {
        this.selectedText.textContent = selectedOption.textContent.trim();
      } else {
        const optionName = this.dropdown.dataset.optionName || 'Size';
        this.selectedText.textContent = `Select ${optionName}`;
      }
    }
    
    handleKeydown(e) {
      switch (e.key) {
        case 'Enter':
        case ' ':
          e.preventDefault();
          this.toggle();
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (!this.isOpen) {
            this.open();
          }
          this.focusNextOption();
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (!this.isOpen) {
            this.open();
          }
          this.focusPreviousOption();
          break;
      }
    }
    
    focusNextOption() {
      const focusedOption = this.menu.querySelector('.custom-dropdown__option:focus');
      if (focusedOption) {
        const nextOption = focusedOption.nextElementSibling;
        if (nextOption) {
          nextOption.focus();
        }
      } else {
        const firstOption = this.menu.querySelector('.custom-dropdown__option:not(.custom-dropdown__option--disabled)');
        if (firstOption) {
          firstOption.focus();
        }
      }
    }
    
    focusPreviousOption() {
      const focusedOption = this.menu.querySelector('.custom-dropdown__option:focus');
      if (focusedOption) {
        const previousOption = focusedOption.previousElementSibling;
        if (previousOption) {
          previousOption.focus();
        }
      }
    }
  }
  
  // Initialize custom dropdowns when DOM is loaded
  document.addEventListener('DOMContentLoaded', function() {
    const customDropdowns = document.querySelectorAll('.custom-dropdown');
    customDropdowns.forEach(dropdown => {
      new CustomDropdown(dropdown);
    });
    
    // Override the variant system to handle our custom dropdown
    setTimeout(overrideVariantSystem, 100);
    
    // Sync custom dropdowns with existing variant selection
    setTimeout(syncCustomDropdowns, 500);
    
    // Listen for variant changes to update custom dropdown availability
    setupVariantChangeListener();
  });
  
  // Function to sync custom dropdowns with existing variant selection
  function syncCustomDropdowns() {
    const customDropdowns = document.querySelectorAll('.custom-dropdown');
    customDropdowns.forEach(dropdown => {
      const variantContainer = dropdown.closest('variant-radios, variant-selects');
      if (!variantContainer) return;
      
      // Find the corresponding radio button or select that's currently selected
      const optionName = dropdown.dataset.optionName || 'Size';
      const variantInputs = variantContainer.querySelectorAll(`input[name*="${optionName}"], select[name*="${optionName}"]`);
      
      variantInputs.forEach(input => {
        if ((input.type === 'radio' && input.checked) || (input.type !== 'radio' && input.value)) {
          const selectedValue = input.value;
          const hiddenInput = dropdown.querySelector('input[type="hidden"]');
          const selectedText = dropdown.querySelector('.custom-dropdown__selected-text');
          const selectedOption = dropdown.querySelector(`.custom-dropdown__option[data-value="${selectedValue}"]`);
          
          if (hiddenInput && selectedOption && !selectedOption.classList.contains('custom-dropdown__option--disabled')) {
            hiddenInput.value = selectedValue;
            selectedText.textContent = selectedOption.textContent.trim();
            
            // Update visual selection
            dropdown.querySelectorAll('.custom-dropdown__option').forEach(opt => 
              opt.classList.remove('custom-dropdown__option--selected')
            );
            selectedOption.classList.add('custom-dropdown__option--selected');
          }
        }
      });
    });
  }
  
  // Function to override the variant system's updateOptions method
  function overrideVariantSystem() {
    const variantContainers = document.querySelectorAll('variant-radios, variant-selects');
    
    variantContainers.forEach(container => {
      // Store the original updateOptions method
      if (!container._originalUpdateOptions) {
        container._originalUpdateOptions = container.updateOptions.bind(container);
        
        // Override the updateOptions method
        container.updateOptions = function() {
          // Call the original method first
          this._originalUpdateOptions();
          
          console.log('Original options after updateOptions:', this.options);
          
          // Then update with our custom dropdown values
          const customDropdowns = this.querySelectorAll('.custom-dropdown');
          customDropdowns.forEach(dropdown => {
            const optionName = dropdown.dataset.optionName || 'Size';
            const hiddenInput = dropdown.querySelector('input[type="hidden"]');
            if (!hiddenInput || !hiddenInput.value) return;
            
            // Find the correct position in the options array
            // We need to determine which option position this is based on the product structure
            let optionPosition = -1;
            
            // Get the variant data to understand the option order
            const variantData = this.getVariantData();
            if (variantData && variantData.length > 0) {
              const firstVariant = variantData[0];
              
              console.log('First variant structure:', {
                option1: firstVariant.option1,
                option2: firstVariant.option2,
                option3: firstVariant.option3,
                options: firstVariant.options
              });
              
              // Determine the position based on the variant structure
              // Check which option position contains size values
              if (optionName === 'Size') {
                const sizeValues = ['Small', 'Medium', 'Large', 'Extra Large', 'XL', 'L', 'M', 'S'];
                
                // Check if Size is option1, option2, or option3
                if (firstVariant.option1 && sizeValues.some(size => firstVariant.option1.includes(size))) {
                  optionPosition = 0;
                } else if (firstVariant.option2 && sizeValues.some(size => firstVariant.option2.includes(size))) {
                  optionPosition = 1;
                } else if (firstVariant.option3 && sizeValues.some(size => firstVariant.option3.includes(size))) {
                  optionPosition = 2;
                }
                
                console.log(`Determined Size is at position ${optionPosition} based on variant structure`);
              }
            }
            
            // If we found the position, update the options array
            if (optionPosition >= 0) {
              // Ensure the options array is long enough
              while (this.options.length <= optionPosition) {
                this.options.push(undefined);
              }
              
              this.options[optionPosition] = hiddenInput.value;
              console.log(`Updated options[${optionPosition}] = ${hiddenInput.value} for ${optionName}`);
            } else {
              console.log(`Could not find position for ${optionName}`);
            }
          });
          
          console.log('Final options array:', this.options);
        };
        
        // Also override updateMasterId to debug variant matching
        if (!container._originalUpdateMasterId) {
          container._originalUpdateMasterId = container.updateMasterId.bind(container);
          
          container.updateMasterId = function() {
            console.log('updateMasterId called with options:', this.options);
            
            const variantData = this.getVariantData();
            console.log('Available variants:', variantData.map(v => ({ id: v.id, options: v.options, available: v.available })));
            
            // Call original method
            this._originalUpdateMasterId();
            
            console.log('Selected variant:', this.currentVariant ? { id: this.currentVariant.id, options: this.currentVariant.options } : 'none');
          };
        }
        
        console.log('Overrode updateOptions method for variant container');
      }
    });
  }
  
  // Function to ensure custom dropdown radio buttons are properly set
  function updateCustomDropdownRadios() {
    // First, override the variant system if not already done
    overrideVariantSystem();
    
    const customDropdowns = document.querySelectorAll('.custom-dropdown');
    customDropdowns.forEach(dropdown => {
      const variantContainer = dropdown.closest('variant-radios, variant-selects');
      if (!variantContainer) return;
      
      const optionName = dropdown.dataset.optionName || 'Size';
      const hiddenInput = dropdown.querySelector('input[type="hidden"]');
      if (!hiddenInput) return;
      
      const selectedValue = hiddenInput.value;
      
      console.log(`Custom dropdown ${optionName} has value: ${selectedValue}`);
    });
  }
  
  // Function to update custom dropdown availability when variants change
  function updateCustomDropdownAvailability() {
    const customDropdowns = document.querySelectorAll('.custom-dropdown');
    customDropdowns.forEach(dropdown => {
      const variantContainer = dropdown.closest('variant-radios, variant-selects');
      if (!variantContainer) return;
      
      // Get variant data
      const variantScript = variantContainer.querySelector('script[type="application/json"]');
      if (!variantScript) return;
      
      let variantData;
      try {
        variantData = JSON.parse(variantScript.textContent);
      } catch (e) {
        console.error('Error parsing variant data:', e);
        return;
      }
      
      if (!variantData || variantData.length === 0) return;
      
      // Determine which option position the dropdown corresponds to
      const optionName = dropdown.dataset.optionName || 'Size';
      let dropdownOptionPosition = -1;
      
      // Get all option elements in order (both fieldsets and dropdowns)
      const allOptionElements = variantContainer.querySelectorAll('fieldset.product-form__input, .product-form__input--dropdown');
      
      // Find which position this dropdown is at among all options
      allOptionElements.forEach((optionElement, index) => {
        const dropdownElement = optionElement.querySelector('.custom-dropdown');
        if (dropdownElement === dropdown) {
          dropdownOptionPosition = index; // 0-based index
        }
      });
      
      console.log(`Dropdown "${optionName}" is at position ${dropdownOptionPosition}`);
      
      // Get currently selected values for all options
      const currentSelections = {};
      
      allOptionElements.forEach((optionElement, index) => {
        const optionPosition = index + 1; // Convert to 1-based for option1, option2, option3
        
        // Check if this is a fieldset with radio buttons
        const checkedInput = optionElement.querySelector('input[type="radio"]:checked');
        if (checkedInput) {
          currentSelections[`option${optionPosition}`] = checkedInput.value;
          console.log(`Option ${optionPosition} (fieldset): ${checkedInput.value}`);
        } else {
          // Check if this is a dropdown container
          const dropdownElement = optionElement.querySelector('.custom-dropdown');
          // Only include other dropdowns, not the one we're currently updating
          if (dropdownElement && dropdownElement !== dropdown) {
            const hiddenInput = dropdownElement.querySelector('input[type="hidden"]');
            if (hiddenInput && hiddenInput.value) {
              currentSelections[`option${optionPosition}`] = hiddenInput.value;
              console.log(`Option ${optionPosition} (dropdown): ${hiddenInput.value}`);
            }
          }
        }
      });
      
      console.log('Current selections:', currentSelections);
      
      // Track if the currently selected option in the dropdown is available
      const hiddenInput = dropdown.querySelector('input[type="hidden"]');
      const currentlySelectedValue = hiddenInput ? hiddenInput.value : null;
      let currentlySelectedIsAvailable = false;
      
      // Update availability for each option in the custom dropdown
      const dropdownOptions = dropdown.querySelectorAll('.custom-dropdown__option');
      dropdownOptions.forEach(option => {
        const optionValue = option.dataset.value;
        
        // Check if this option value is available with current selections
        let isAvailable = false;
        
        for (let variant of variantData) {
          let matches = true;
          
          // Check all option positions
          for (let i = 1; i <= 3; i++) {
            const optionKey = `option${i}`;
            
            if (i - 1 === dropdownOptionPosition) {
              // This is the dropdown's position - check against the option value we're testing
              if (variant[optionKey] !== optionValue) {
                matches = false;
                break;
              }
            } else if (currentSelections[optionKey]) {
              // This is another option - check against current selection
              if (variant[optionKey] !== currentSelections[optionKey]) {
                matches = false;
                break;
              }
            }
          }
          
          if (matches && variant.available) {
            isAvailable = true;
            break;
          }
        }
        
        console.log(`Option "${optionValue}" is ${isAvailable ? 'available' : 'unavailable'}`);
        
        // Update option availability
        if (isAvailable) {
          option.classList.remove('custom-dropdown__option--disabled');
          option.removeAttribute('aria-disabled');
          option.textContent = optionValue;
        } else {
          option.classList.add('custom-dropdown__option--disabled');
          option.setAttribute('aria-disabled', 'true');
          option.textContent = `${optionValue} - Unavailable`;
        }
        
        // Track if the currently selected option is available
        if (optionValue === currentlySelectedValue) {
          currentlySelectedIsAvailable = isAvailable;
        }
      });
      
      // Update the trigger's selected text to reflect current availability
      const selectedText = dropdown.querySelector('.custom-dropdown__selected-text');
      
      if (selectedText && currentlySelectedValue) {
        console.log(`Updating trigger text for "${currentlySelectedValue}": ${currentlySelectedIsAvailable ? 'available' : 'unavailable'}`);
        
        if (currentlySelectedIsAvailable) {
          // Show just the value if available
          selectedText.textContent = currentlySelectedValue;
        } else {
          // Show unavailable status if not available
          selectedText.textContent = `${currentlySelectedValue} - Unavailable`;
        }
        
        console.log('Trigger text updated to:', selectedText.textContent);
      } else {
        console.log('Could not update trigger text:', { selectedText: !!selectedText, currentlySelectedValue });
      }
    });
    
    console.log('updateCustomDropdownAvailability completed');
  }
  
  // Function to setup variant change listener
  function setupVariantChangeListener() {
    // Listen for variant change events - this is the main one that should fire
    document.addEventListener('change', function(e) {
      // Check if it's a variant option change (either in variant-radios or variant-selects)
      const variantContainer = e.target.closest('variant-radios, variant-selects');
      if (variantContainer) {
        // Check if it's an option input/select
        const isVariantInput = e.target.matches('input[type="radio"]') || 
                              e.target.matches('select[name*="options"]') ||
                              e.target.matches('input[type="hidden"][name*="options"]');
        
        if (isVariantInput) {
          console.log('Variant change detected:', e.target.name, e.target.value);
          console.log('Updating dropdown availability...');
          
          // Update availability immediately after the change event
          setTimeout(updateCustomDropdownAvailability, 50);
        }
      }
    });
    
    // Also listen for custom variant change events from Shopify's variant system
    document.addEventListener('variant:change', function() {
      console.log('Custom variant change event detected');
      setTimeout(updateCustomDropdownAvailability, 50);
    });
    
    // Listen for label clicks which trigger variant changes
    document.addEventListener('click', function(e) {
      // Check if a label for a variant radio button was clicked
      if (e.target.matches('label[for]')) {
        const radioId = e.target.getAttribute('for');
        const radio = document.getElementById(radioId);
        
        if (radio && radio.matches('input[type="radio"]')) {
          const variantContainer = radio.closest('variant-radios, variant-selects');
          if (variantContainer) {
            console.log('Label clicked for radio button:', radio.name, radio.value);
            
            // Update after the radio button has been checked and change event fired
            setTimeout(updateCustomDropdownAvailability, 100);
          }
        }
      }
    });
  }
  
  // Re-initialize when new content is added (for dynamic content)
  document.addEventListener('shopify:section:load', function() {
    const customDropdowns = document.querySelectorAll('.custom-dropdown:not([data-initialized])');
    customDropdowns.forEach(dropdown => {
      dropdown.setAttribute('data-initialized', 'true');
      new CustomDropdown(dropdown);
    });
    
    // Sync custom dropdowns with existing variant selection
    setTimeout(syncCustomDropdowns, 100);
    
    // Re-wrap quantity and button containers
    wrapQuantityAndButtons();
  });
  
   // Function to create flex container and ensure button takes full width
   function forceButtonWidth() {
     const quantitySelectors = document.querySelectorAll('.product-form__quantity');
     
     quantitySelectors.forEach((quantitySelector, index) => {
       // Check if already wrapped
       const existingWrapper = quantitySelector.closest('.product-form__quantity-and-button-wrapper');
       if (existingWrapper) {
        
        // Update styles on existing wrapper
        const button = existingWrapper.querySelector('.product-form__submit');
        const buttonsContainer = existingWrapper.querySelector('.product-form__buttons');
        
       // Update wrapper alignment
       existingWrapper.style.setProperty('align-items', 'center', 'important');
       existingWrapper.style.setProperty('height', 'auto', 'important');
       
       if (button) {
         button.style.setProperty('width', '100%', 'important');
         button.style.setProperty('max-width', 'none', 'important');
         button.style.setProperty('min-width', '0', 'important');
         button.style.setProperty('flex', '1', 'important');
       }
       
       if (buttonsContainer) {
         buttonsContainer.style.setProperty('width', '100%', 'important');
         buttonsContainer.style.setProperty('max-width', 'none', 'important');
         buttonsContainer.style.setProperty('min-width', '0', 'important');
         buttonsContainer.style.setProperty('flex', '1', 'important');
         buttonsContainer.style.setProperty('align-self', 'center', 'important');
         buttonsContainer.style.setProperty('height', '5.6rem', 'important'); // Fixed height to match quantity selector
         buttonsContainer.style.setProperty('padding-top', '0', 'important'); // Remove padding
       }
       
       // Update quantity selector alignment
       quantitySelector.style.setProperty('align-self', 'center', 'important');
       quantitySelector.style.setProperty('height', '5.6rem', 'important'); // Fixed height to match button container
       
       // Make product-form and form elements fit content
       const productForm = existingWrapper.querySelector('product-form');
       if (productForm) {
         productForm.style.setProperty('height', 'fit-content', 'important');
         productForm.style.setProperty('display', 'block', 'important');
       }
       
       const formElement = existingWrapper.querySelector('form');
       if (formElement) {
         formElement.style.setProperty('height', 'fit-content', 'important');
         formElement.style.setProperty('display', 'block', 'important');
       }
        
        return;
      }
      
      // Find the next sibling that contains buy buttons
      let nextElement = quantitySelector.nextElementSibling;
      let buyButtonsContainer = null;
      
      // Look for the buy buttons container in the next few siblings
      while (nextElement && !buyButtonsContainer) {
        if (nextElement.querySelector && nextElement.querySelector('.product-form__buttons')) {
          buyButtonsContainer = nextElement;
          break;
        }
        if (nextElement.classList && nextElement.classList.contains('product-form__buttons')) {
          buyButtonsContainer = nextElement;
          break;
        }
        nextElement = nextElement.nextElementSibling;
      }
      
       if (buyButtonsContainer) {
        
         // Create wrapper container
         const wrapper = document.createElement('div');
         wrapper.className = 'product-form__quantity-and-button-wrapper';
         wrapper.style.display = 'flex';
         wrapper.style.gap = '1.5rem';
         wrapper.style.marginBottom = '1.6rem';
         wrapper.style.alignItems = 'center';
         wrapper.style.width = '100%';
         wrapper.style.height = 'auto'; // Let it size to content
        
        // Insert wrapper before quantity selector
        quantitySelector.parentNode.insertBefore(wrapper, quantitySelector);
        
        // Move quantity selector and buy buttons into wrapper
        wrapper.appendChild(quantitySelector);
        wrapper.appendChild(buyButtonsContainer);
        
         // Apply styles to elements inside wrapper
         quantitySelector.style.marginRight = '0';
         quantitySelector.style.marginBottom = '0';
         quantitySelector.style.flex = '0 0 auto';
         quantitySelector.style.maxWidth = '12rem';
         quantitySelector.style.alignSelf = 'center';
         quantitySelector.style.height = '5.6rem'; // Match button height
         
         buyButtonsContainer.style.marginBottom = '0';
         buyButtonsContainer.style.flex = '1';
         buyButtonsContainer.style.width = 'auto';
         buyButtonsContainer.style.maxWidth = 'none';
         buyButtonsContainer.style.minWidth = '0';
         buyButtonsContainer.style.height = '5.6rem'; // Match quantity selector height
         buyButtonsContainer.style.alignSelf = 'center';
         buyButtonsContainer.style.paddingTop = '0'; // Remove padding since we're using fixed height
         
         // Make the button container fit the button height
         const productForm = buyButtonsContainer.querySelector('product-form');
         if (productForm) {
           productForm.style.height = 'fit-content';
           productForm.style.display = 'block';
         }
         
         const formElement = buyButtonsContainer.querySelector('form');
         if (formElement) {
           formElement.style.height = 'fit-content';
           formElement.style.display = 'block';
         }
        
        // Style the button inside
        const button = buyButtonsContainer.querySelector('.product-form__submit');
        if (button) {
          // More aggressive styling
          button.style.setProperty('width', '100%', 'important');
          button.style.setProperty('max-width', 'none', 'important');
          button.style.setProperty('min-width', '0', 'important');
          button.style.setProperty('flex', '1', 'important');
          button.style.setProperty('display', 'block', 'important');
          button.style.setProperty('position', 'relative', 'important');
          button.style.setProperty('top', '2px', 'important');
          
          // Also set on the buttons container
          buyButtonsContainer.style.setProperty('width', '100%', 'important');
          buyButtonsContainer.style.setProperty('max-width', 'none', 'important');
          buyButtonsContainer.style.setProperty('min-width', '0', 'important');
          buyButtonsContainer.style.setProperty('flex', '1', 'important');
        }
      }
    });
  }
  
  // Initialize on page load
  document.addEventListener('DOMContentLoaded', function() {
    setTimeout(forceButtonWidth, 100);
    
    // Run every 500ms to catch any dynamic changes
    setInterval(forceButtonWidth, 500);
  });
  
  // Force button width when variants change
  document.addEventListener('variant:change', function() {
    setTimeout(forceButtonWidth, 100);
  });
  
  // Listen for Shopify's variant change events (from global.js)
  document.addEventListener('variantChange', function() {
    setTimeout(forceButtonWidth, 100);
  });
  
  // Listen for any custom events that might affect the form
  document.addEventListener('shopify:section:load', function() {
    setTimeout(forceButtonWidth, 100);
  });
  
  // Also listen for DOM mutations that might affect the button
  document.addEventListener('DOMContentLoaded', function() {
    // Run more frequently to catch any dynamic changes
    setInterval(forceButtonWidth, 1000);
  });
  
  // Force button width on any DOM changes
  const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.type === 'childList' || mutation.type === 'attributes') {
        setTimeout(forceButtonWidth, 100);
      }
    });
  });
  
  // Start observing
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style', 'class']
  });
  